import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getProducts, deleteProduct, updateProduct } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Plus,
  Search,
  Trash2,
  Pencil,
  Filter,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
} from 'lucide-react';

export default function ProductList() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [partType, setPartType] = useState('');
  const [condition, setCondition] = useState('');
  const [sort, setSort] = useState('createdAt_desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const PART_TYPES = ['Engine & Drivetrain', 'Bodywork', 'Electrical', 'Suspension & Wheels', 'Apparel', 'Accessories'];
  const CONDITIONS = ['New', 'Used – Normal Use', 'Used – Fair', 'NOS'];

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: 10, sort };
    if (searchTerm) params.search = searchTerm;
    if (partType) params.partType = partType;
    if (condition) params.condition = condition;
    getProducts(params)
      .then(data => { setProducts(data.products); setPagination(data.pagination); })
      .finally(() => setLoading(false));
  }, [page, sort, partType, condition, searchTerm]);

  const handleSearch = () => {
    setSearchTerm(search);
    setPage(1);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === products.length && products.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(products.map(p => p._id));
    }
  };

  const bulkToggleAvailability = async () => {
    const newState = !products.every(p => selectedIds.every(id => {
      const prod = products.find(p => p._id === id);
      return prod && prod.isAvailable;
    }));
    try {
      await Promise.all(selectedIds.map(id =>
        updateProduct(id, { isAvailable: newState })
      ));
      setSelectedIds([]);
      const params = { page, limit: 10, sort };
      if (partType) params.partType = partType;
      if (condition) params.condition = condition;
      getProducts(params).then(data => { setProducts(data.products); setPagination(data.pagination); });
    } catch (err) {
      alert('Failed to update availability');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await deleteProduct(id);
      setProducts(products.filter(p => p._id !== id));
      setSelectedIds(prev => prev.filter(i => i !== id));
    } catch (err) {
      alert('Failed to delete product');
    }
  };

  const resetFilters = () => {
    setSearch('');
    setSearchTerm('');
    setPartType('');
    setCondition('');
    setPage(1);
  };

  const hasActiveFilters = partType || condition || searchTerm;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Products</h1>
        <Button onClick={() => navigate('/admin/products/add')}>
          <Plus className="w-4 h-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search products..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    className="pl-9"
                  />
                </div>
              </div>
              <Button onClick={handleSearch} variant="default">
                Search
              </Button>

              <Select value={partType} onValueChange={v => { setPartType(v); setPage(1); }}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Part Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Types</SelectItem>
                  {PART_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={condition} onValueChange={v => { setCondition(v); setPage(1); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Condition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Conditions</SelectItem>
                  {CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="createdAt_desc">Newest First</SelectItem>
                  <SelectItem value="createdAt_asc">Oldest First</SelectItem>
                  <SelectItem value="price_asc">Price: Low to High</SelectItem>
                  <SelectItem value="price_desc">Price: High to Low</SelectItem>
                  <SelectItem value="title_asc">Name: A-Z</SelectItem>
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button variant="outline" onClick={resetFilters}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={selectedIds.length === products.length && products.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
              </TableHead>
              <TableHead className="w-28">SKU</TableHead>
              <TableHead>Title</TableHead>
              <TableHead className="w-36">Type</TableHead>
              <TableHead className="w-32">Condition</TableHead>
              <TableHead className="w-28">Price</TableHead>
              <TableHead className="w-16 text-center">Stock</TableHead>
              <TableHead className="w-28">Status</TableHead>
              <TableHead className="w-32 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-sm text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-sm text-slate-400">
                  No products found.
                </TableCell>
              </TableRow>
            ) : (
              products.map(p => (
                <TableRow key={p._id}>
                  <td>
                    <Checkbox
                      checked={selectedIds.includes(p._id)}
                      onCheckedChange={() => toggleSelect(p._id)}
                    />
                  </td>
                  <td className="font-mono text-sm text-slate-500">{p.sku}</td>
                  <td>
                    <Link
                      to={`/product/${p._id}`}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:underline cursor-pointer"
                    >
                      {p.title}
                    </Link>
                  </td>
                  <td className="text-sm text-slate-600">{p.partCategory}</td>
                  <td>
                    <Badge
                      variant="default"
                      className={
                        p.condition === 'New' ? 'bg-green-100 text-green-700' :
                        p.condition === 'NOS' ? 'bg-blue-100 text-blue-700' :
                        p.condition === 'Used – Normal Use' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }
                    >
                      {p.condition}
                    </Badge>
                  </td>
                  <td className="font-semibold text-slate-900">
                    ${p.price.toLocaleString('en-CA')}
                  </td>
                  <td className="text-center text-slate-500">{p.stock}</td>
                  <td>
                    <Badge variant={p.isAvailable ? 'default' : 'destructive'}>
                      {p.isAvailable ? 'Available' : 'Unavailable'}
                    </Badge>
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/admin/products/${p._id}/edit`}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-indigo-600">
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-600"
                        onClick={() => handleDelete(p._id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Bulk Actions Bar */}
        {selectedIds.length > 0 && (
          <div className="flex items-center gap-3 px-6 py-3 bg-indigo-50 border-t border-indigo-100 text-sm font-medium text-indigo-600">
            <span>{selectedIds.length} selected</span>
            <Button variant="outline" size="sm" onClick={bulkToggleAvailability}>
              Toggle Availability
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSelectedIds([])}>
              Clear Selection
            </Button>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-1 px-6 py-4">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
              let pageNum;
              if (pagination.pages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= pagination.pages - 2) {
                pageNum = pagination.pages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={page === pageNum ? 'default' : 'outline'}
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
            <Button
              variant="outline"
              size="sm"
              disabled={page === pagination.pages}
              onClick={() => setPage(p => p + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}